import {skipToken} from '@reduxjs/toolkit/query';
import {Action, Card, DataStatus, openDialog} from 'platform/components';
import {HStack, Icon, Text, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {useListPurchasePriceRulesQuery} from '@dms/api/sales';
import {i18n} from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsSection} from '@dms/settings/shared';
import {usePermissions} from '@dms/shared';

import {buildArray} from 'shared/web';

import {NewPurchasePriceRuleDialog} from './components/NewPurchasePriceRuleDialog';
import {PurchasePriceRuleList} from './components/PurchasePriceRuleList';

const DATA_STATUS_MIN_HEIGHT = 40;

export function PurchasePriceRules() {
  const [canRead, canManage] = usePermissions({
    permissionKeys: ['readPurchasePriceRules', 'managePurchasePriceRules'],
  });

  const {
    data: rules,
    isLoading,
    isError,
    refetch,
  } = useListPurchasePriceRulesQuery(canRead ? undefined : skipToken);

  if (!canRead) {
    return null;
  }

  const handleNewRule = () => {
    if (isNil(rules)) {
      return;
    }

    openDialog(<NewPurchasePriceRuleDialog rules={rules} />, {
      title: i18n.t('page.settings.purchasePrice.marginRules.newRule'),
      'data-testid': testIds.settings.purchasePrice('newRuleDialog'),
    });
  };

  const actions = buildArray<Action>().when(canManage && !isNil(rules), {
    type: 'button',
    variant: 'primary',
    leftIcon: 'content/add',
    title: i18n.t('page.settings.purchasePrice.marginRules.newRule'),
    onClick: handleNewRule,
    'data-testid': testIds.settings.purchasePrice('newRule'),
  });

  return (
    <SettingsSection>
      <Card
        title={i18n.t('page.settings.purchasePrice.marginRules.title')}
        actions={actions}
        data-testid={testIds.settings.purchasePrice('marginRules')}
      >
        <DataStatus
          data={rules}
          isLoading={isLoading}
          isError={isError}
          minHeight={DATA_STATUS_MIN_HEIGHT}
          errorAction={{
            title: i18n.t('general.actions.retry'),
            onClick: refetch,
          }}
          data-testid={testIds.settings.purchasePrice('marginRulesStatus')}
        >
          {({data}) => (
            <VStack spacing={4}>
              <VStack spacing={2}>
                <Text size="small" color="secondary">
                  {i18n.t('page.settings.purchasePrice.marginRules.description')}
                </Text>
                <HStack spacing={2} align="flex-start">
                  {/* Centred on the first 20px line of the small text. */}
                  <HStack height={5} align="center" shrink={0}>
                    <Icon value="action/info" size={4} color="palettes.neutral.60.100" />
                  </HStack>
                  <Text
                    size="small"
                    color="secondary"
                    data-testid={testIds.settings.purchasePrice('modifiersNote')}
                  >
                    {i18n.t('page.settings.purchasePrice.marginRules.modifiersNote')}
                  </Text>
                </HStack>
              </VStack>

              <PurchasePriceRuleList rules={data} canManage={canManage} />
            </VStack>
          )}
        </DataStatus>
      </Card>
    </SettingsSection>
  );
}
